import { createAsyncThunk } from "@reduxjs/toolkit";
import centerApiService, {
  AddStudentsToAttendanceBody,
  AddStudentsToScheduleBody,
  BlockedStudentBody,
  CorrectExamBody,
  CreateScheduleBodySave,
  LecturePayloadSave,
  MarkIndividualStudentAttendanceBody,
  noteHandlerBody,
  ScheduleLecturesParams,
  ScheduleParams,
  StudentScheduleParams,
  TeacherCenterAttendancesParams,
  TeacherCenterLecturesParamsBase,
  warningHandlerBody,
} from "../services/center";
import {
  AddStudentToAttendance,
  AttendanceResponseWithPagination,
  AttendanceStudent,
  CenterLecture,
  CentersPaginatedResponse,
  CourseParsed,
  CreateResponse,
  EnrollmentsPaginatedResponse,
  LecturesResponseWithPagination,
  ResponseApi,
  ScheduleDetails,
  ScheduleLecturesPaginatedResponse,
  SchedulesPaginatedResponse,
  Student,
  StudentAttendanceDetailsResponseApi,
  TinyLecture,
  TinySchedule,
} from "@/types/center";
import { warning } from "framer-motion";

const getTeacherCenterPagination = createAsyncThunk<
  CentersPaginatedResponse,
  { page?: number; search?: string }
>(
  "centers/fetchPaginated",
  async (
    { page = 1, search = "" }: { page?: number; search?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await centerApiService.getTeacherCenterPagination({
        page,
        search,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const createCenter = createAsyncThunk<CreateResponse, { name: string }>(
  "centers/createCenter",
  async (data: { name: string }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.createCenter(data);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const updateCenter = createAsyncThunk<
  CreateResponse,
  { id: number; body: { name: string } }
>("centers/updateCenter", async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await centerApiService.updateCenter(id, body);
    return response?.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const deleteCenter = createAsyncThunk<CreateResponse, { id: number }>(
  "centers/deleteCenter",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.deleteCenter(id);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getSchedulesWithPagination = createAsyncThunk<
  SchedulesPaginatedResponse, // return type
  ScheduleParams // argument type
>(
  "centers/getSchedulesPagination",
  async ({ center_id, page, search }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getTeacherCenterSchedules({
        center_id,
        page,
        search,
      });

      return response.data as SchedulesPaginatedResponse;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

const getStudentSchedule = createAsyncThunk<
  EnrollmentsPaginatedResponse,
  StudentScheduleParams
>(
  "centers/getStudentSchedule",
  async ({ page, schedule_id, search }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getStudentSchedule({
        page,
        schedule_id,
        search,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getScheduleLectures = createAsyncThunk<
  ScheduleLecturesPaginatedResponse,
  ScheduleLecturesParams
>(
  "centers/getScheduleLectures",
  async ({ page, search }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getScheduleLectures({
        page,
        search,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const createSchedule = createAsyncThunk<CreateResponse, CreateScheduleBodySave>(
  "centers/createSchedule",
  async (body, { rejectWithValue }) => {
    try {
      const response = await centerApiService.createSchedule(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const updateSchedule = createAsyncThunk<
  CreateResponse,
  { id: number; body: CreateScheduleBodySave }
>("centers/updateSchedule", async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await centerApiService.updateSchedule(id, body);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const deleteSchedule = createAsyncThunk<CreateResponse, { id: number }>(
  "centers/deleteSchedule",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.deleteSchedule(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getStudentsForSelectOption = createAsyncThunk<
  ResponseApi<Student[]>,
  string
>(
  "centers/getStudentsForSelectOption",
  async (search: string, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getStudentsForSelectOption(
        search
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const addStudentsToSchedule = createAsyncThunk<
  CreateResponse,
  AddStudentsToScheduleBody
>("centers/addStudentsToSchedule", async (body, { rejectWithValue }) => {
  try {
    const response = await centerApiService.addStudentsToSchedule(body);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});
const studentBlocked = createAsyncThunk<CreateResponse, BlockedStudentBody>(
  "centers/blockedStudent",
  async (body, { rejectWithValue }) => {
    try {
      const response = await centerApiService.blockedStudent(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

const getStudentDetails = createAsyncThunk<
  StudentAttendanceDetailsResponseApi,
  { scheduleId: number; userId: number; is_attended: number | null }
>(
  "centers/getStudentDetails",
  async ({ scheduleId, userId, is_attended }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getStudentDetails(
        scheduleId,
        userId,
        is_attended
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getTeacherCenterLectures = createAsyncThunk<
  LecturesResponseWithPagination,
  TeacherCenterLecturesParamsBase
>("centers/getTeacherCenterLectures", async (params, { rejectWithValue }) => {
  try {
    const response = await centerApiService.getTeacherCenterLectures(params);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const createLecture = createAsyncThunk<CreateResponse, LecturePayloadSave>(
  "centers/createLecture",
  async (body, { rejectWithValue }) => {
    try {
      const response = await centerApiService.createLecture(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getTeacherCenterLecturesForSelect = createAsyncThunk<
  ResponseApi<TinyLecture[]>
>(
  "centers/getTeacherCenterLecturesForSelect",
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await centerApiService.getTeacherCenterLecturesForSelect();
      // Ensure response.data is an array of TinyLecture
      return response.data;
      // If response.data is a single TinyLecture, wrap it in an array
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getCoursesParsed = createAsyncThunk<ResponseApi<CourseParsed[]>>(
  "centers/getCoursesParsed",
  async (_, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getCoursesParsed();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const getCenterCoursesParsed = createAsyncThunk<ResponseApi<CourseParsed[]>>(
  "centers/getCenterCoursesParsed",
  async (_, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getCenterCoursesParsed();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getSchedulesWithoutPagination = createAsyncThunk<
  ResponseApi<TinySchedule[]>
>("centers/getSchedulesWithoutPagination", async (_, { rejectWithValue }) => {
  try {
    const response = await centerApiService.getSchedulesWithoutPagination();
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const getTeacherLectureDetails = createAsyncThunk<
  ResponseApi<CenterLecture>,
  number | undefined
>("centers/getTeacherLectureDetails", async (id, { rejectWithValue }) => {
  try {
    const response = await centerApiService.getTeacherLectureDetails(id);
    return response?.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const updateLecture = createAsyncThunk<
  CreateResponse,
  { id: number; body: LecturePayloadSave }
>("centers/updateLecture", async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await centerApiService.updateLecture({ body, id });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});
const getAttendanceLectureWithPagination = createAsyncThunk<
  AttendanceResponseWithPagination,
  TeacherCenterAttendancesParams
>(
  "centers/getAttendanceLectureWithPagination",
  async (
    {
      is_attended,
      page,
      per_page,
      schedule_lecture_id,
      search,
      forced,
      lecture_id,
      warning,
    },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await centerApiService.getAttendanceLectureWithPagination({
          is_attended,
          page,
          per_page,
          schedule_lecture_id,
          search,
          forced,
          lecture_id,
          warning,
        });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const getTeacherLectureDetailsForSelect = createAsyncThunk<any, number>(
  "centers/getTeacherLectureDetailsForSelect",
  async (id, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getTeacherLectureDetailsForSelect(
        id
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const recording = createAsyncThunk<any, number>(
  "centers/recording",
  async (id, { rejectWithValue }) => {
    try {
      const response = await centerApiService.Recording(id);
      // console.log(response, "from thunk");
      return response.data;
    } catch (error: any) {
      // console.log(error ,"Error from the Thunk");

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const eligibleStudents = createAsyncThunk<
  ResponseApi<Student[]>,
  {
    search?: string;
    lectureId: number;
    scheduleLectureId: number;
  }
>(
  "centers/eligibleStudents",
  async ({ lectureId, scheduleLectureId, search }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.eligibleStudents({
        lectureId,
        scheduleLectureId,
        search,
      });
      // console.log(response, "from thunk");
      return response.data;
    } catch (error: any) {
      // console.log(error ,"Error from the Thunk");

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const markIndividualStudentAttendance = createAsyncThunk<
  CreateResponse,
  MarkIndividualStudentAttendanceBody
>(
  "centers/markIndividualStudentAttendance",
  async (body, { rejectWithValue }) => {
    try {
      const response = await centerApiService.markIndividualStudentAttendance(
        body
      );
      // console.log(response, "from thunk");
      return response.data;
    } catch (error: any) {
      // console.log(error ,"Error from the Thunk");

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const getSchedule = createAsyncThunk<ResponseApi<ScheduleDetails>, number>(
  "centers/getSchedule",
  async (schedule_id, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getSchedule(schedule_id);
      // console.log(response, "from thunk");
      return response.data;
    } catch (error: any) {
      // console.log(error ,"Error from the Thunk");

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const getStudentAllDetails = createAsyncThunk<
  StudentAttendanceDetailsResponseApi,
  { userId?: number; is_attended?: boolean }
>(
  "centers/getStudentAllDetails",
  async ({ userId, is_attended }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getStudentAllDetails({
        userId,
        is_attended,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const correctExam = createAsyncThunk<CreateResponse, CorrectExamBody>(
  "centers/correctExam",
  async (body, { rejectWithValue }) => {
    try {
      const response = await centerApiService.correctExam(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const noteHandler = createAsyncThunk<CreateResponse, noteHandlerBody>(
  "centers/noteHandler",
  async (body, { rejectWithValue }) => {
    try {
      const response = await centerApiService.noteHandler(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const warningHandler = createAsyncThunk<CreateResponse, warningHandlerBody>(
  "centers/warningHandler",
  async (body, { rejectWithValue }) => {
    try {
      const response = await centerApiService.warningHandler(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const markAllPresent = createAsyncThunk<CreateResponse, number>(
  "centers/markAllPresent",
  async (schedule_lecture_id, { rejectWithValue }) => {
    try {
      const response = await centerApiService.markAllPresent(
        schedule_lecture_id
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const markAllAbsent = createAsyncThunk<CreateResponse, number>(
  "centers/markAllAbsent",
  async (schedule_lecture_id, { rejectWithValue }) => {
    try {
      const response = await centerApiService.markAllAbsent(
        schedule_lecture_id
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const addStudentsToAttendance = createAsyncThunk<
  CreateResponse,
  AddStudentsToAttendanceBody
>("centers/addStudentsToAttendance", async (body, { rejectWithValue }) => {
  try {
    const response = await centerApiService.addStudentsToAttendance(body);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});
const getStudentForSelectToPreparePage = createAsyncThunk<
  ResponseApi<AddStudentToAttendance[]>,
  {
    lectureId: number;
    scheduleLectureId: number;
    search?: string;
  }
>(
  "centers/getStudentForSelectToPreparePage",
  async ({ lectureId, scheduleLectureId, search }, { rejectWithValue }) => {
    try {
      const response = await centerApiService.getStudentForSelectToPreparePage({
        lectureId,
        scheduleLectureId,
        search,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const centerThunks = {
  addStudentsToAttendance,
  getStudentForSelectToPreparePage,
  markAllAbsent,
  markAllPresent,
  correctExam,
  noteHandler,
  warningHandler,
  getStudentAllDetails,
  getSchedule,
  eligibleStudents,
  markIndividualStudentAttendance,
  recording,
  getTeacherLectureDetailsForSelect,
  getAttendanceLectureWithPagination,
  updateLecture,
  getTeacherLectureDetails,
  getCenterCoursesParsed,
  getSchedulesWithoutPagination,
  getTeacherCenterLecturesForSelect,
  createLecture,
  getTeacherCenterLectures,
  getCoursesParsed,
  studentBlocked,
  getStudentsForSelectOption,
  addStudentsToSchedule,
  getStudentSchedule,
  getScheduleLectures,
  getStudentAttendanceDetails: getStudentDetails,
  createSchedule,
  getSchedulesWithPagination,
  getTeacherCenterPagination,
  updateSchedule,
  deleteSchedule,
  deleteCenter,
  createCenter,
  updateCenter,
};

export default centerThunks;
